export const whatIsWeb3Data = [
  {
    title: "The Evolution of the Web",
    content: [
      {
        type: "heading",
        text: "From Web1 to Web3: A Journey of Transformation"
      },
      {
        type: "paragraph",
        text: "The internet has evolved dramatically over the past three decades, transforming from a simple information-sharing platform to a complex ecosystem that shapes every aspect of our digital lives. Understanding this evolution is crucial to grasping what Web3 represents and why it matters."
      },
      {
        type: "paragraph",
        text: "Web3 represents the next phase in this evolution, promising to fundamentally change how we interact with the internet, own digital assets, and participate in online communities. It's not just a technological upgrade—it's a paradigm shift that challenges the very foundations of how the digital world operates."
      }
    ]
  },
  {
    title: "Web1: The Read-Only Web (1990s-2000s)",
    content: [
      {
        type: "heading",
        text: "The Static Internet"
      },
      {
        type: "paragraph",
        text: "Web1, also known as the \"Static Web\" or \"Read-Only Web,\" was the internet's first iteration. During this period, the web was primarily a one-way communication medium where users could consume information but had limited ability to contribute or interact."
      },
      {
        type: "paragraph",
        text: "Key characteristics of Web1:"
      },
      {
        type: "list",
        items: [
          "Static Content: Websites were primarily HTML pages with text and basic images",
          "Limited Interactivity: Users could read content but couldn't easily contribute",
          "Centralized Control: Content was controlled by website owners and webmasters",
          "Basic Functionality: Simple forms, basic search, and minimal user-generated content",
          "Slow Connections: Dial-up internet limited multimedia content"
        ]
      },
      {
        type: "paragraph",
        text: "Web1 was revolutionary for its time, making information accessible globally and establishing the foundation for digital communication. However, it was limited by its static nature and lack of user participation."
      }
    ]
  },
  {
    title: "Web2: The Social Web (2000s-2020s)",
    content: [
      {
        type: "heading",
        text: "The Interactive Internet"
      },
      {
        type: "paragraph",
        text: "Web2, often called the \"Social Web\" or \"Read-Write Web,\" transformed the internet into an interactive platform where users could not only consume content but also create, share, and collaborate. This era gave birth to social media, cloud computing, and mobile applications."
      },
      {
        type: "paragraph",
        text: "Key features of Web2:"
      },
      {
        type: "list",
        items: [
          "User-Generated Content: Social media platforms, blogs, and forums",
          "Interactive Applications: Dynamic websites with rich user experiences",
          "Mobile Revolution: Smartphones and mobile apps transformed access",
          "Cloud Computing: Centralized data storage and processing",
          "Social Networking: Facebook, Twitter, Instagram, and other platforms",
          "E-commerce Boom: Online shopping and digital marketplaces"
        ]
      },
      {
        type: "paragraph",
        text: "While Web2 brought unprecedented connectivity and user engagement, it also created new challenges around data privacy, platform monopolies, and user control over personal information."
      }
    ]
  },
  {
    title: "Web3: The Decentralized Web (2020s and Beyond)",
    content: [
      {
        type: "heading",
        text: "The Ownership Internet"
      },
      {
        type: "paragraph",
        text: "Web3, also known as the \"Decentralized Web\" or \"Ownership Web,\" represents a fundamental shift toward a more open, decentralized internet where users have greater control over their data, identity, and digital assets. Built on blockchain technology, Web3 promises to address many of the limitations and challenges of Web2."
      },
      {
        type: "paragraph",
        text: "Core principles of Web3:"
      },
      {
        type: "list",
        items: [
          "Decentralization: No single entity controls the network or data",
          "User Ownership: Users own their data, identity, and digital assets",
          "Transparency: Open-source protocols and transparent operations",
          "Permissionless: Anyone can participate without gatekeepers",
          "Trustless: Systems operate without requiring trust in intermediaries",
          "Interoperability: Different platforms and services can work together"
        ]
      }
    ]
  },
  {
    title: "Key Technologies Powering Web3",
    content: [
      {
        type: "heading",
        text: "The Technical Foundation"
      },
      {
        type: "paragraph",
        text: "Web3 is built on several key technologies that work together to create a decentralized internet:"
      },
      {
        type: "list",
        items: [
          "Blockchain: Distributed ledger technology for secure, transparent transactions",
          "Smart Contracts: Self-executing contracts with terms directly written into code",
          "Cryptocurrencies: Digital currencies that enable value transfer without intermediaries",
          "NFTs: Non-fungible tokens representing unique digital ownership",
          "Decentralized Storage: IPFS and other systems for distributed file storage",
          "Digital Wallets: Tools for managing digital assets and identity"
        ]
      }
    ]
  },
  {
    title: "Web3 Use Cases and Applications",
    content: [
      {
        type: "heading",
        text: "Real-World Applications"
      },
      {
        type: "paragraph",
        text: "Web3 is already enabling new types of applications and services:"
      },
      {
        type: "list",
        items: [
          "Decentralized Finance (DeFi): Banking services without traditional banks",
          "NFT Marketplaces: Platforms for buying, selling, and trading digital assets",
          "Decentralized Autonomous Organizations (DAOs): Community-governed organizations",
          "Play-to-Earn Games: Gaming where players can earn real value",
          "Social Media: Platforms where users own their content and data",
          "Decentralized Identity: Self-sovereign identity management"
        ]
      }
    ]
  },
  {
    title: "Challenges and Future Outlook",
    content: [
      {
        type: "heading",
        text: "Overcoming Obstacles"
      },
      {
        type: "paragraph",
        text: "While Web3 holds great promise, it also faces several challenges that need to be addressed for widespread adoption:"
      },
      {
        type: "list",
        items: [
          "Scalability: Current blockchain networks can be slow and expensive",
          "User Experience: Complex interfaces and technical barriers",
          "Regulatory Uncertainty: Evolving legal frameworks and compliance",
          "Energy Consumption: Environmental concerns with some blockchain networks",
          "Security Risks: Smart contract vulnerabilities and scams",
          "Interoperability: Connecting different blockchain networks"
        ]
      },
      {
        type: "paragraph",
        text: "Despite these challenges, the Web3 ecosystem continues to evolve rapidly, with new solutions and improvements being developed constantly. The future of Web3 looks promising as it moves toward greater accessibility, sustainability, and real-world utility."
      }
    ]
  }
];
